import {
  type AnalyticsEvent,
  type AnalyticsProps,
  sanitizeAnalyticsEvent,
  sanitizeScreenPath,
} from "@netkeep/legal";
import posthog from "posthog-js";
import { APP_VERSION, POSTHOG_HOST, POSTHOG_KEY } from "./config";
import { KEYS, readJson, writeJson } from "./storage";

/**
 * Anonymous usage analytics (PostHog), exactly as described in the Privacy Policy
 * (@netkeep/legal, sections 4 and 5), same as the mobile app:
 *  - off entirely unless VITE_POSTHOG_KEY is set at build time;
 *  - users can turn it off in Settings; it starts off when the browser sends
 *    Do Not Track or Global Privacy Control;
 *  - no cookies (localStorage only), no autocapture, no session recording;
 *  - only the allow-listed events in @netkeep/legal, sanitized so no typed amount is ever sent.
 */
let started = false;

/** True when this build has a PostHog key, so the Settings switch means something. */
export const analyticsAvailable = !!POSTHOG_KEY;

/** Do Not Track or Global Privacy Control. */
function browserOptOutSignal(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true || nav.doNotTrack === "1";
}

export function analyticsEnabled(): boolean {
  return readJson<boolean>(KEYS.analyticsEnabled) ?? !browserOptOutSignal();
}

/** Starts PostHog once, in the browser only. Does nothing without a key. */
export function initAnalytics(): void {
  if (!POSTHOG_KEY || started || typeof window === "undefined") return;
  started = true;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    persistence: "localStorage",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    disable_surveys: true,
    advanced_disable_flags: true,
    opt_out_capturing_by_default: !analyticsEnabled(),
  });
  posthog.register({ app: "web", app_version: APP_VERSION });
}

export function track<E extends AnalyticsEvent>(event: E, props?: AnalyticsProps<E>): void {
  if (!started || !analyticsEnabled()) return;
  posthog.capture(event, sanitizeAnalyticsEvent(event, props));
}

export function trackPageview(path: string): void {
  if (!started || !analyticsEnabled()) return;
  posthog.capture("$pageview", { $current_url: `${window.location.origin}${sanitizeScreenPath(path)}` });
}

/** This browser's random analytics ID, shown in Settings so users can ask us to delete their data. */
export function analyticsId(): string | null {
  return started ? posthog.get_distinct_id() || null : null;
}

export function setAnalyticsEnabled(enabled: boolean): void {
  writeJson(KEYS.analyticsEnabled, enabled);
  if (!started) return;
  if (enabled) posthog.opt_in_capturing();
  else posthog.opt_out_capturing();
}
